import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { useNumericParams } from '@domain-group/react-hooks'

import {
  Box, Button, Columns, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import quoteAndFinanceTabBarItems from '@sections/Client/packs/Campaign/utils/generateQuoteAndFinanceTabBarItems'

import useCampaign from '@hooks/useCampaign'

import CampaignFee from '@components/CampaignFee'
import FinanceApplications from '@components/FinanceApplications'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PendingInvoices from '@components/PendingInvoices'
import ReconciliationTotals from '@components/ReconciliationTotals'
import StatementHistory from '@components/StatementHistory'

import PageContext from '@contexts/pageContext'

import useCampaignAccounting from '@hooks/useCampaignAccounting'

import StatementHistoryBlankState from '../StatementHistoryBlankState'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        defaultSelectLabel: 'Show All',
        style,
      },
      key: 'itemType',
      label: 'Select Option',
      values: [
        { key: 'bill', label: 'Bill' },
        { key: 'campaign_balance', label: 'Campaign Balance' },
        { key: 'credit', label: 'Credit' },
        { key: 'invoice', label: 'Invoice' },
        { key: 'payment', label: 'Payment' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {
   itemType?: string,
   string?: string,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const { campaignId } = useNumericParams()

  const { campaign, campaign: { address }, loading: loadingCampaign } = useCampaign({ id: campaignId })

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showAddAccountingItemModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const campaignAccountingPayload = useCampaignAccounting({
    agencyId: campaign?.agency_id,
    campaignId,
    filters: { ...pageFilters },
    performHttpRequests: !!campaignId,
  })

  const {
    callbacks, filteredStatementItems, hasFilteredStatementItems, hasStatementItems, loading,
  } = campaignAccountingPayload

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Accounting</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showAddAccountingItemModal({ campaign })} size="medium">
            Create Item
          </Button>
        )}
        activeTabBarItemKey="accounting"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={quoteAndFinanceTabBarItems(campaignId)}
        title="Accounting"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.accounting.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasStatementItems && (
              <StatementHistoryBlankState />
            )}

            {!loading && !loadingCampaign && hasStatementItems && (
              <StatementHistory
                callbacks={callbacks}
                campaign={campaign}
                hasFilteredStatementItems={hasFilteredStatementItems}
                // @ts-expect-error TS2322
                items={filteredStatementItems}
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.accounting.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />

            {!loadingCampaign && (
              <ReconciliationTotals
                campaign={campaign}
                displayKeys={[
                  'approved_invoice_total',
                  'credit_note_total',
                  'contribution_total',
                  'received_contribution_total',
                  'campaign_balance',
                ]}
              />
            )}

            <CampaignFee campaign={campaign} />

            <PendingInvoices accountingPayload={campaignAccountingPayload} campaign={campaign} />

            <FinanceApplications campaignId={campaignId} />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}
export default PageContent
